import React, { useCallback, useContext, useMemo, useState } from "react";
import { CompanyAuth } from "../../entities/login/CompanyAuth";
import { ReportExportListResponse } from "../../entities/reports/ReportExport/ReportExportListResponse";
import { ReportExportSetRequest } from "../../entities/reports/ReportExport/ReportExportSetRequest";
import CompanyService, { Entities } from "../../services/CompanyService";
import InvoiceService from "../../services/InvoiceService";
import ReportExportService from "../../services/ReportExportService";
import { TranslationService } from "../../services/TranslationService";
import { sheetColumns } from "../company/Configuration/ImportTemplate/ImportTemplateEditBase";
import ToastContext from "../shared/bootstrap/Toast";
import Dropdown from "../shared/components/Dropdown";
import { CheckBoxEditor, TextEditor } from "../shared/components/Editors";
import FloatingPanelService from "../shared/FloatingPanel";
import { FloatingPanelFooter } from "../shared/components/FloatingPanelFooter";
import { RequiredManager, ValidationMessage } from "../shared/RequieredManager";
import { toEvent } from "../../utils/FuncUtils";
import { isNullOrWhitespace } from "../../utils/Utils";
import TooltipComponent from "../shared/TooltipComponent";

const ReportExportEdit = ({
  data,
  reload
}: {
  data: ReportExportListResponse.Item;
  reload: () => void;
}) => {
  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);
  const [model, setModel] = useState<Partial<ReportExportSetRequest.Item>>(
    createModel(data)
  );
  const [submiting, setSubmiting] = useState<"deleting" | "saving">();
  const [search, setSearch] = useState("");
  const requiredManager = new RequiredManager();
  const readOnlyForm = !CompanyService.canEditReportExport();

  const reportTypes = [
    translate.Activity,
    translate.Invoice,
    translate.Customer,
    translate.Activity + "-" + translate.Invoice,
    "",
    translate.Payment
  ];

  const reportTypesFields = [
    {
      /*Activity*/
      // Incluye campos person-* porque el reporte de actividad puede mostrar información agregada del cliente
      builder: activityFields(clientFields(new FieldBuilder(model), false)),
      showInvoiceCheckbox: false,
      includeLastActivityCheckbox: false
    },
    {
      /* Invoice */
      // Excluye campos person-* de la sección de cliente porque:
      // - Los campos person-* son agregados de facturas, no datos básicos del cliente
      // - Ya existe una sección "Datos de Factura" con información detallada
      // - Evita duplicación y confusión en la interfaz
      builder: invoiceFields(clientFields(new FieldBuilder(model), true)),
      showInvoiceCheckbox: true,
      includeLastActivityCheckbox: true
    },
    {
      /* Customer */
      // Incluye campos person-* porque en reportes de cliente tiene sentido mostrar
      // información agregada de facturas del cliente
      builder: clientFields(new FieldBuilder(model), false),
      showInvoiceCheckbox: false,
      includeLastActivityCheckbox: true
    },
    {
      /* Activity-Invoice */
      // Excluye campos person-* por la misma razón que en reportes de factura
      builder: invoiceFields(clientFields(new FieldBuilder(model), true)),
      showInvoiceCheckbox: false,
      includeLastActivityCheckbox: true
    },
    {
      builder: new FieldBuilder(undefined),
      showInvoiceCheckbox: false,
      includeLastActivityCheckbox: false
    },
    {
      /* Payment */
      // Excluye campos person-* porque el reporte de pago se enfoca en transacciones,
      // no en información agregada de facturas del cliente
      builder: invoiceFields(
        paymentFields(clientFields(new FieldBuilder(model), true))
      ),
      showInvoiceCheckbox: false,
      includeLastActivityCheckbox: false
    }
  ];
  const baseBuilder = useMemo(() => {
    const type = data.report;
    const fb = new FieldBuilder(model);
    switch (type) {
      case 0: // Activity
        // Incluye campos person-* para mostrar información agregada del cliente
        return activityFields(clientFields(fb, false));
      case 1: // Invoice
        // Excluye campos person-* de la sección de cliente en reportes de factura
        // para evitar mostrar información agregada de facturas en la sección de cliente
        return invoiceFields(clientFields(fb, true));
      case 2: // Customer
        // Incluye campos person-* porque en reportes de cliente tiene sentido
        // mostrar información agregada de facturas del cliente
        return clientFields(fb, false);
      case 3: // Activity-Invoice
        // Excluye campos person-* por la misma razón que en reportes de factura
        return invoiceFields(clientFields(fb, true));
      case 5: // Payment
        // Excluye campos person-* porque el reporte de pago se enfoca en transacciones
        return invoiceFields(paymentFields(clientFields(fb, true)));
      default:
        return new FieldBuilder(undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.report, model.columns]);

  const reportBody = reportTypesFields[data.report];
  const hasAnyActivitySelected = useMemo(() => {
    return (model.columns ?? []).some(
      (c) => c.field?.startsWith("activity-") && c.Position != null
    );
  }, [model.columns]);
  const composedBuilder = useMemo(() => {
    if (model.IncludeLastActivity || hasAnyActivitySelected) {
      const fb = new FieldBuilder(model);
      fb.fields = { ...baseBuilder.fields };
      return activityFields(fb);
    }
    return baseBuilder;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseBuilder, model, model.IncludeLastActivity, hasAnyActivitySelected]);

  const onSubmit = async () => {
    if (!requiredManager.validate()) {
      return;
    }
    if (submiting) {
      return;
    }

    if (model.columns === undefined || model.columns.length === 0) {
      showToast(translate.MissingRequiredFields);
      return;
    }

    const modelFormated: any = {
      ...model,
      columns: (model as any).columns.map((el: any) =>
        el.field === "" && el.AdditionalDefinitionID
          ? { ...el, field: "additional-" + el.AdditionalDefinitionID }
          : el
      )
    };
    setSubmiting("saving");
    const result = await ReportExportService.set(
      modelFormated as ReportExportSetRequest.Item
    );
    setSubmiting(undefined);
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
      return;
    }
    reload();
    FloatingPanelService.hidePanel();
  };

  const onDelete = async () => {
    if (submiting) {
      return;
    }
    setSubmiting("deleting");
    const result = await ReportExportService.delete(model.ReportExportID!);
    setSubmiting(undefined);
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
      return;
    }
    reload();
    FloatingPanelService.hidePanel();
  };

  const updateColumn = useCallback((field: FieldData, value?: string) => {
    setModel((prev) => {
      let columns =
        prev.columns?.filter((c) => c.field !== field.fieldId) ?? [];
      const isAdditional = field.fieldId.startsWith("additional-");
      const additionalId = isAdditional ? parseInt(field.key, 10) : undefined;
      if (isAdditional) {
        columns = (columns as any).filter(
          (c: any) => c.AdditionalDefinitionID !== additionalId
        );
      }

      const parsed =
        value != null && value !== "" ? parseInt(value, 10) : undefined;
      if (parsed != null && !Number.isNaN(parsed)) {
        const entry: any = { field: field.fieldId, Position: parsed };
        if (isAdditional) entry.AdditionalDefinitionID = additionalId;
        columns = [...columns, entry];
      }

      return { ...prev, columns };
    });
  }, []);

  return (
    <>
      <div className="floatingBody">
        <div className="d-flex justify-content-between align-items-center">
          <label className="col-4 font-weight-bold">{translate.Type}</label>
          <div className="col">{reportTypes[data.report]}</div>
        </div>

        <div className="d-flex justify-content-between align-items-center my-2">
          <label className="col-4 font-weight-bold">{translate.Name}</label>
          {!readOnlyForm ? (
            <div className="col">
              <TextEditor
                onChange={requiredManager.makeRequiredWithId(
                  (x) =>
                    setModel(
                      (m) => ({ ...m, Name: x }) as ReportExportSetRequest.Item
                    ),
                  "NameId"
                )}
                value={model.Name ?? ""}
              />
              {
                <ValidationMessage
                  onChange={requiredManager.getValidation("NameId")!}
                  defaultValue={model.Name}
                />
              }
            </div>
          ) : (
            <div className="col">{data.Name}</div>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <TooltipComponent title={translate.ProtectedTip}>
            <label className="col-4 font-weight-bold">
              {translate.Protected}
              <i className="fal fa-question-square px-2" />
            </label>
          </TooltipComponent>
          <div className="col">
            <CheckBoxEditor
              disabled={readOnlyForm}
              message={<></>}
              onChange={(x) =>
                setModel((model) => ({ ...model, Protected: x }))
              }
              defaultValue={data.Protected}
            />
          </div>
        </div>
        {reportBody.showInvoiceCheckbox && (
          <>
            <div className="d-flex justify-content-between align-items-center">
              <label className="col-4 font-weight-bold">
                {translate.Public}
                <i
                  className="fal fa-question-square px-2"
                  title={translate.PublicTip}
                />
              </label>
              <div className="col">
                <CheckBoxEditor
                  disabled={readOnlyForm}
                  message={<></>}
                  onChange={(x) =>
                    setModel((model) => ({ ...model, Public: x }))
                  }
                  defaultValue={data.Public}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <label className="col-4 font-weight-bold">
                {translate.CustomerSite}
                <i
                  className="fal fa-question-square px-2"
                  title={translate.CustomerSiteTip}
                />
              </label>
              <div className="col">
                <CheckBoxEditor
                  disabled={readOnlyForm}
                  message={<></>}
                  onChange={(x) =>
                    setModel((model) => ({ ...model, Portal: x }))
                  }
                  defaultValue={data.Portal}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <label className="col-4 font-weight-bold">
                {translate.Default}
                <i
                  className="fal fa-question-square px-2"
                  title={translate.DefaultTip}
                />
              </label>
              <div className="col">
                <CheckBoxEditor
                  disabled={readOnlyForm}
                  message={<></>}
                  onChange={(x) =>
                    setModel((model) => ({ ...model, Default: x }))
                  }
                  defaultValue={data.Default}
                />
              </div>
            </div>
          </>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <label className="col-4 font-weight-bold">
            {TranslationService.translate.Search +
              " " +
              TranslationService.translate.Field.toLocaleLowerCase()}
          </label>
          <div className="col">
            <input
              className="form-control"
              onChange={toEvent((x) => setSearch(x))}
            />
          </div>
        </div>
        {Object.entries(composedBuilder.fields)
          .filter(
            ([section]) => section !== TranslationService.translate.Activity
          )
          .map(([sectionTitle, fieldData]) => (
            <ExportFields
              key={`section-${data.report}-${sectionTitle}`}
              fieldData={fieldData}
              title={sectionTitle}
              readOnlyForm={readOnlyForm}
              updateColumn={updateColumn}
              columns={model.columns}
              isPublic={model.Public}
              search={search}
            />
          ))}
        {reportBody.includeLastActivityCheckbox && (
          <>
            <div className="row align-items-center mt-3">
              <label className="col-auto w-xl col-form-label font-weight-normal">
                {translate.IncludeLastActivity}{" "}
              </label>
              <div className="col mt-1">
                <CheckBoxEditor
                  disabled={readOnlyForm}
                  message={<></>}
                  onChange={(x) =>
                    setModel((model) => ({ ...model, IncludeLastActivity: x }))
                  }
                  defaultValue={data.IncludeLastActivity}
                />
              </div>
            </div>
          </>
        )}
        {Object.entries(composedBuilder.fields)
          .filter(
            ([section]) => section === TranslationService.translate.Activity
          )
          .map(([sectionTitle, fieldData]) => (
            <ExportFields
              key={`section-${data.report}-${sectionTitle}`}
              fieldData={fieldData}
              title={sectionTitle}
              readOnlyForm={readOnlyForm}
              updateColumn={updateColumn}
              columns={model.columns}
              isPublic={model.Public}
              search={search}
            />
          ))}
      </div>
      <FloatingPanelFooter>
        {!readOnlyForm && (
          <>
            {data.ReportExportID !== undefined && data.ReportExportID > 0 && (
              <button className="btn btn-danger me-auto" onClick={onDelete}>
                {translate.Delete}
                {submiting === "deleting" && (
                  <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                )}
              </button>
            )}

            <button className="btn btn-primary me-3" onClick={onSubmit}>
              {translate.Save}
              {submiting === "saving" && (
                <i className="fas fa-spinner-third fa-spin third ms-2"></i>
              )}
            </button>
          </>
        )}
      </FloatingPanelFooter>
    </>
  );
};

export type FieldData = {
  fieldId: string;
  key: string;
  label: string;
  value: string;
};

class FieldBuilder {
  fields: { [key: string]: FieldData[] } = {};
  constructor(public data: Partial<ReportExportSetRequest.Item> | undefined) {}
}

function createFieldData(
  key: string,
  label: string,
  data?: Partial<ReportExportSetRequest.Item>
): FieldData {
  return {
    fieldId: key,
    key: key,
    label: label,
    value:
      data?.columns
        ?.find((x) => x.field.toLowerCase() === key.toLowerCase())
        ?.Position.toString() ?? ""
  };
}

function additionalDefsToFieldData(
  additionalDefs: CompanyAuth.AdditionalDefinition[],
  data: Partial<ReportExportSetRequest.Item> | undefined
) {
  return additionalDefs.map((x) => ({
    fieldId: "additional-" + x.AdditionalDefinitionID.toString(),
    key: x.AdditionalDefinitionID.toString(),
    label: x.Name,
    value:
      data?.columns
        ?.find(
          (y: any) =>
            y.AdditionalDefinitionID?.toString() ===
            x.AdditionalDefinitionID.toString()
        )
        ?.Position.toString() ?? ""
  }));
}

/**
 * Crea los campos de la sección "Datos de Cliente" para los reportes de exportación.
 *
 * CAMBIO IMPLEMENTADO:
 * Se agregó el parámetro `excludePersonFields` para controlar si se incluyen los campos
 * agregados de facturas (person-count, person-amount, person-dueamount, person-nodueamount).
 *
 * RAZÓN DEL CAMBIO:
 * Estos campos (person-*) representan información agregada de facturas del cliente (cantidad
 * de facturas, monto total, monto vencido, monto no vencido). Cuando el tipo de reporte es
 * "Factura" (Invoice), estos campos no deberían aparecer en la sección "Datos de Cliente"
 * porque:
 * 1. Son campos agregados relacionados con facturas, no datos básicos del cliente
 * 2. Ya existe una sección específica "Datos de Factura" que muestra información detallada
 * 3. Evita duplicación y confusión en la interfaz
 * 4. La sección "Datos de Cliente" debe mostrar solo información básica del cliente
 *
 * Los campos person-* se mantienen para reportes de tipo "Cliente" (Customer) donde tiene
 * sentido mostrar información agregada de facturas del cliente.
 *
 * @param fieldBuilder - Constructor de campos que contiene el modelo de datos
 * @param excludePersonFields - Si es true, excluye los campos agregados de facturas (person-*)
 *                              de la sección de cliente. Útil para reportes de factura donde
 *                              estos campos no son relevantes en la sección de cliente.
 */
function clientFields(
  fieldBuilder: FieldBuilder,
  excludePersonFields: boolean = false
) {
  const name = TranslationService.translate.Customer;

  // Campos básicos del cliente (siempre incluidos)
  const baseFields = [
    createFieldData(
      "name",
      TranslationService.translate.Name,
      fieldBuilder.data
    ),
    createFieldData(
      "email",
      TranslationService.translate.Email,
      fieldBuilder.data
    ),
    createFieldData(
      "address",
      TranslationService.translate.Address,
      fieldBuilder.data
    ),
    createFieldData(
      "comments",
      TranslationService.translate.Comments,
      fieldBuilder.data
    ),
    createFieldData(
      "personcontact",
      TranslationService.translate.Contact,
      fieldBuilder.data
    ),
    createFieldData(
      "phone",
      TranslationService.translate.Phone,
      fieldBuilder.data
    ),
    createFieldData(
      "followupmail",
      TranslationService.translate.FollowUpMail,
      fieldBuilder.data
    ),
    createFieldData(
      "customersite",
      TranslationService.translate.CustomerSite,
      fieldBuilder.data
    ),
    createFieldData(
      "creditlimit",
      TranslationService.translate.CreditLimit,
      fieldBuilder.data
    ),
    createFieldData(
      "segments",
      TranslationService.translate.Segments,
      fieldBuilder.data
    )
  ];

  // Campos agregados de facturas del cliente (person-*)
  // Estos campos representan información agregada de facturas:
  // - person-count: Cantidad total de facturas del cliente
  // - person-amount: Monto total de facturas del cliente
  // - person-dueamount: Monto vencido de facturas del cliente
  // - person-nodueamount: Monto no vencido de facturas del cliente
  //
  // IMPLEMENTACIÓN ANTERIOR (comentada):
  // Estos campos se incluían siempre en la sección de cliente, lo que causaba
  // que aparecieran en reportes de factura donde no son relevantes.
  //
  // createFieldData("person-count", TranslationService.translate.TypeIOList, fieldBuilder.data),
  // createFieldData("person-amount", TranslationService.translate.Amount, fieldBuilder.data),
  // createFieldData("person-dueamount", TranslationService.translate.DueAmount, fieldBuilder.data),
  // createFieldData("person-nodueamount", TranslationService.translate.NotDueAmount, fieldBuilder.data),

  // NUEVA IMPLEMENTACIÓN:
  // Solo se incluyen estos campos cuando excludePersonFields es false (reportes de cliente)
  // Se excluyen cuando excludePersonFields es true (reportes de factura, activity-invoice, payment)
  if (!excludePersonFields) {
    baseFields.push(
      createFieldData(
        "person-count",
        TranslationService.translate.TypeIOList,
        fieldBuilder.data
      ),
      createFieldData(
        "person-amount",
        TranslationService.translate.Amount,
        fieldBuilder.data
      ),
      createFieldData(
        "person-dueamount",
        TranslationService.translate.DueAmount,
        fieldBuilder.data
      ),
      createFieldData(
        "person-nodueamount",
        TranslationService.translate.NotDueAmount,
        fieldBuilder.data
      )
    );
  }

  fieldBuilder.fields[name] = baseFields;
  fieldBuilder.fields[name].push(
    ...additionalDefsToFieldData(
      CompanyService.getAdditionalDefinitions().filter(
        (x) => x.Entity === Entities.Client
      ),
      fieldBuilder.data
    )
  );
  return fieldBuilder;
}

function activityFields(fieldBuilder: FieldBuilder) {
  const name = TranslationService.translate.Activity;
  fieldBuilder.fields[name] = [
    createFieldData("activity-id", "Id", fieldBuilder.data),
    createFieldData(
      "activity-date",
      TranslationService.translate.Date,
      fieldBuilder.data
    ),
    createFieldData(
      "activity-user",
      TranslationService.translate.User,
      fieldBuilder.data
    ),
    createFieldData(
      "activity-type",
      TranslationService.translate.Type,
      fieldBuilder.data
    ),
    createFieldData(
      "activity-activitytype",
      TranslationService.translate.ActivityType,
      fieldBuilder.data
    ),
    createFieldData(
      "activity-content",
      TranslationService.translate.Content,
      fieldBuilder.data
    ),
    createFieldData(
      "activity-tags",
      TranslationService.translate.Tags,
      fieldBuilder.data
    ),
    createFieldData(
      "activity-created",
      TranslationService.translate.Created,
      fieldBuilder.data
    ),
    createFieldData(
      "activity-aa",
      TranslationService.translate.AutomatedActions,
      fieldBuilder.data
    )
  ];
  return fieldBuilder;
}

function invoiceFields(fieldBuilder: FieldBuilder) {
  const name = TranslationService.translate.Invoice;
  fieldBuilder.fields[name] = [
    createFieldData("group", CompanyService.getGroupName(), fieldBuilder.data),
    createFieldData(
      "createdDate",
      TranslationService.translate.IssueDate,
      fieldBuilder.data
    ),
    createFieldData(
      "referenceNumber",
      InvoiceService.getReferenceNumberName(),
      fieldBuilder.data
    ),
    createFieldData(
      "currency",
      TranslationService.translate.Currency,
      fieldBuilder.data
    ),
    createFieldData(
      "amount",
      TranslationService.translate.Amount,
      fieldBuilder.data
    ),
    createFieldData(
      "pending",
      TranslationService.translate.Pending,
      fieldBuilder.data
    ),
    createFieldData(
      "currencyrate",
      TranslationService.translate.ExchangeRate,
      fieldBuilder.data
    ),
    createFieldData(
      "dueDate",
      TranslationService.translate.DueDate,
      fieldBuilder.data
    ),
    createFieldData(
      "dueDays",
      TranslationService.translate.OverdueDays,
      fieldBuilder.data
    ),
    createFieldData(
      "completeddate",
      TranslationService.translate.PaidDate,
      fieldBuilder.data
    ),
    createFieldData(
      "iostatus",
      TranslationService.translate.IOStatus,
      fieldBuilder.data
    ),
    createFieldData(
      "description",
      TranslationService.translate.Comments,
      fieldBuilder.data
    )
  ];
  fieldBuilder.fields[name].push(
    ...additionalDefsToFieldData(
      CompanyService.getAdditionalDefinitions().filter(
        (x) => x.Entity === Entities.Invoice
      ),
      fieldBuilder.data
    )
  );
  return fieldBuilder;
}

function paymentFields(fieldBuilder: FieldBuilder) {
  const name = TranslationService.translate.Payment;
  fieldBuilder.fields[name] = [
    createFieldData(
      "transaction-Amount",
      TranslationService.translate.Amount,
      fieldBuilder.data
    ),
    createFieldData(
      "transaction-Date",
      TranslationService.translate.Date,
      fieldBuilder.data
    ),
    createFieldData(
      "transaction-Comments",
      TranslationService.translate.Comments,
      fieldBuilder.data
    ),
    createFieldData(
      "transaction-TransactionType",
      TranslationService.translate.PaymentType,
      fieldBuilder.data
    ),
    createFieldData(
      "transaction-User",
      TranslationService.translate.User,
      fieldBuilder.data
    ),
    createFieldData(
      "transaction-Receipt",
      TranslationService.translate.Receipt,
      fieldBuilder.data
    )
  ];
  fieldBuilder.fields[name].push(
    ...additionalDefsToFieldData(
      CompanyService.getAdditionalDefinitions().filter(
        (x) => x.Entity === Entities.Payment
      ),
      fieldBuilder.data
    )
  );
  return fieldBuilder;
}

function createModel(
  data: ReportExportListResponse.Item
):
  | Partial<ReportExportSetRequest.Item>
  | (() => Partial<ReportExportSetRequest.Item>) {
  if (data === undefined) {
    return {};
  }
  return { ...data, columns: data.ReportExportFields };
}

const ExportFields = React.memo(function ExportFields({
  fieldData,
  title,
  readOnlyForm,
  updateColumn,
  columns,
  isPublic,
  search
}: {
  fieldData: FieldData[];
  title: string;
  readOnlyForm: boolean;
  updateColumn: (field: FieldData, value?: string) => void;
  columns: NonNullable<ReportExportSetRequest.Item["columns"]> | undefined;
  isPublic: boolean | undefined;
  search: string;
}) {
  const fields = useMemo(() => {
    const searchUpper = (search ?? "").toUpperCase();
    return fieldData.filter(
      (x) =>
        isNullOrWhitespace(search) ||
        x.label.toUpperCase().includes(searchUpper)
    );
  }, [fieldData, search]);

  return (
    <div>
      <h6 className="mt-3 font-weight-bold">
        {TranslationService.translate.FilterData.replace("{EntityName}", title)}
      </h6>

      {fields.map((x) => {
        let currentValue: string | undefined = undefined;

        const fromField = columns?.find((c) => c.field === x.fieldId)?.Position;
        if (fromField != null) {
          currentValue = String(fromField);
        } else if (x.fieldId.startsWith("additional-")) {
          const addId = parseInt(x.key, 10);
          const fromAdd = (columns as any)?.find(
            (c: any) => c.AdditionalDefinitionID === addId
          )?.Position;
          if (fromAdd != null) currentValue = String(fromAdd);
        }

        const used = columns?.map((c) => String(c.Position)) ?? [];

        const items = sheetColumns
          .filter(
            (sc) =>
              !used.includes(String(sc.value)) ||
              String(sc.value) === currentValue
          )
          .map((sc) => ({ text: sc.text, value: String(sc.value) }));

        const safeValue = items.some((it) => it.value === currentValue)
          ? currentValue
          : undefined;

        let showAlert = false;
        if (isPublic && title === TranslationService.translate.Invoice) {
          if (x.fieldId.includes("additional")) {
            const valuePosition = columns?.find(
              (mc) => mc.field === x.fieldId
            )?.Position;
            if (valuePosition) {
              const ShowToClient =
                CompanyService.getCompanyAuth()?.additionalDefinitions.find(
                  (el) => el.AdditionalDefinitionID.toString() === x.key
                )?.ShowToClient;
              showAlert = !Boolean(ShowToClient);
            }
          }
        }

        return (
          <div key={x.fieldId} className="col-12">
            <div className="row w-150 mt-1">
              <label className="px-3 col-form-label font-weight-normal col-4">
                {x.label}
              </label>

              <div className="d-flex col align-items-center">
                <Dropdown
                  readonly={readOnlyForm}
                  onChange={(value) => updateColumn(x, value)}
                  items={items}
                  optionLabel={TranslationService.translate.Select}
                  value={safeValue}
                />

                {showAlert && (
                  <TooltipComponent
                    title={TranslationService.translate.PublicTemplateAlert}
                  >
                    <i className="ms-2 text-danger fas fa-exclamation-square"></i>
                  </TooltipComponent>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default ReportExportEdit;
